import { reportError } from '@pypestream/utils';

import { reportSetupOutcome, type OutcomeDeps } from './knowledge-setup-text';
import {
  failureOfError,
  KnowledgeSetupError,
  type KnowledgeSetupFailure,
} from './run-knowledge-setup';

const RESOURCE_PATH = /projects\/[^\s'"`,;)]+/g;

/**
 * Replaces every `projects/...` resource path with `projects/<masked>`. Returns
 * the same error when nothing matched, so Sentry keeps grouping by the real
 * stack; otherwise a copy carrying the masked message and stack.
 */
export const maskResourcePaths = (error: unknown): unknown => {
  if (!(error instanceof Error)) return error;
  const message = error.message.replace(RESOURCE_PATH, 'projects/<masked>');
  if (message === error.message) return error;
  const copy = new Error(message);
  copy.name = error.name;
  copy.stack = error.stack?.replace(RESOURCE_PATH, 'projects/<masked>');
  return copy;
};

/**
 * Tells the team about a knowledge setup the user was SHOWN failing. Only a
 * failed step is a defect worth a report; the other reasons are the user's own
 * doing (opened another solution, left it, deleted the search node), so
 * reporting them would only add noise.
 *
 * Reports what the failing step threw (a `KnowledgeSetupError` wraps it).
 * `extra` holds counts only: no key or file name, no corpus or project id of
 * the customer's Google Cloud. The error itself is the server's own text, which
 * for a Vertex refusal can name the resource (`projects/<id>/...`), so those
 * resource paths are masked (`maskResourcePaths`); nothing else in the message
 * is scrubbed. Guarded like the other reporters in this app, so a failing
 * reporter cannot turn a shown failure into a crash.
 */
export const reportKnowledgeSetupFailure = (
  thrown: unknown,
  failure: KnowledgeSetupFailure
): void => {
  if (failure.reason !== 'step-failed') return;
  try {
    reportError(
      maskResourcePaths(
        thrown instanceof KnowledgeSetupError ? thrown.original : thrown
      ),
      {
        source: 'knowledge-setup',
        tags: { app: 'pro-studio', degraded: 'true' },
        extra: {
          reason: failure.reason,
          keysAdded: failure.keysAdded,
          keysTotal: failure.keysTotal,
          documents: failure.documents,
          documentsTotal: failure.documentsTotal,
          corpusCreated: !!failure.corpusId,
        },
      }
    );
  } catch {
    // Deliberately empty: telemetry must not break the failure path.
  }
};

/**
 * What the setup chain does when a step threw: tell the team, then show the
 * user the failure. Kept as one function so the pair is exercised by a test
 * instead of living inline in the hook's `.catch`.
 */
export const handleKnowledgeSetupError = (
  err: unknown,
  outcomeDeps: OutcomeDeps
) => {
  const failure = failureOfError(err);
  reportKnowledgeSetupFailure(err, failure);
  return reportSetupOutcome({ failure }, outcomeDeps);
};
